import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, ScanLine, Camera, Plus, Minus, Check, X, Mic,
  Bell, WifiOff, Warehouse, Heart, Package } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { savedDepot, resolveDepot, NO_DEPOT_MSG } from "@/lib/depots";
import { useAuth } from "@/context/AuthContext";
import { supName, supColor, CATEGORY_PILLS } from "@/lib/constants";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ImageZoom } from "@/components/ImageZoom";
import { ScannerCamera } from "@/components/ScannerCamera";

const SupplierBadge = ({ name }) => (
  <span data-testid="supplier-badge" className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: supColor(name) }}>{name}</span>
);
const StockTag = ({ inStock }) => (
  <span data-testid="stock-tag" className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${inStock ? "text-emerald-700 bg-emerald-100" : "text-red-700 bg-red-100"}`}>
    {inStock ? "🟢 In Stock" : "🔴 Out of Stock"}
  </span>
);

export default function Scanner() {
  const { activeStaff } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [listening, setListening] = useState(false);
  const voiceSearch = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice search isn't supported in this browser. Try Chrome, Edge or Safari."); return; }
    const rec = new SR();
    rec.lang = "en-GB"; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onresult = (e) => { const said = e.results[0][0].transcript.trim(); setQuery(said); setNotFound(null); setFocused(null); };
    rec.onerror = (e) => { if (e.error !== "aborted" && e.error !== "no-speech") toast.error("Couldn't hear you — check microphone permission."); };
    rec.onend = () => setListening(false);
    setListening(true); rec.start();
  };
  const [cat, setCat] = useState("offer");
  const [added, setAdded] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [queueN, setQueueN] = useState(0);
  const staffName = activeStaff?.name || "Staff";
  const { t } = useI18n();
  const [fav, setFav] = useState(false);
  const [focused, setFocused] = useState(null);
  const [fqty, setFqty] = useState(1);
  const [fSup, setFSup] = useState(null);
  const [funit, setFunit] = useState("case");
  const [notFound, setNotFound] = useState(null);
  const [fLineId, setFLineId] = useState(null);
  const [ccQty, setCcQty] = useState(0);
  const [ccLineId, setCcLineId] = useState(null);
  const [zoomSrc, setZoomSrc] = useState(null);
  const scannedRef = useRef(null);

  const hydrateFocused = async (pid) => {
    try { const { data } = await api.get("/basket"); const it = (data.groups || []).flatMap((g) => g.items).find((x) => x.product_id === pid); return it ? { qty: it.quantity, lineId: it.id } : { qty: 0, lineId: null }; }
    catch { return { qty: 0, lineId: null }; }
  };
  const hydrateCC = async (prod) => {
    const depot = savedDepot();
    if (!prod || !depot) { setCcQty(0); setCcLineId(null); return; }
    try { const { data } = await api.get("/depot/checklist", { params: { depot } });
      const it = Object.values(data.aisles || {}).flat().find((x) => x.name === prod.name);
      setCcQty(it ? it.quantity : 0); setCcLineId(it ? it.id : null);
    } catch { setCcQty(0); setCcLineId(null); }
  };
  const focusProduct = async (p) => { setFocused(p); setFunit("case"); setFSup(p.best_supplier); setNotFound(null); setQuery(p.barcode || p.name); if (navigator.vibrate) navigator.vibrate(50); const h = await hydrateFocused(p.id); setFqty(h.qty); setFLineId(h.lineId); hydrateCC(p); };
  const clearFocus = () => { setFocused(null); setQuery(""); setNotFound(null); setCcQty(0); setCcLineId(null); scannedRef.current = null; };

  const addOne = async () => {
    try { await api.post("/basket/add", { product_id: focused.id, quantity: 1, unit: funit, variant: "plain", added_by: staffName });
      const h = await hydrateFocused(focused.id); setFqty(h.qty || 1); setFLineId(h.lineId); toast.success(`Added • ${fSup}`); if (navigator.vibrate) navigator.vibrate(40); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const incFocused = async () => { if (!fLineId) return addOne(); try { await api.patch(`/basket/${fLineId}`, { quantity: fqty + 1 }); setFqty(fqty + 1); } catch (e) { toast.error(errMsg(e)); } };
  const decFocused = async () => { if (!fLineId) return; const n = fqty - 1; try { if (n <= 0) { await api.delete(`/basket/${fLineId}`); setFqty(0); setFLineId(null); toast.success("Removed from cart"); } else { await api.patch(`/basket/${fLineId}`, { quantity: n }); setFqty(n); } } catch (e) { toast.error(errMsg(e)); } };

  const addCC = async () => {
    try { const depot = await resolveDepot(); if (!depot) return toast.error(NO_DEPOT_MSG);
      await api.post("/depot/add", { name: focused.name, aisle: focused.category || "Grocery", price: focused.depot_price ?? focused.best_price, quantity: 1, depot, category: focused.category, photo: focused.image, tag: "C&C", barcode: focused.barcode });
      await hydrateCC(focused); toast.success(`Added to C&C (${depot})`); if (navigator.vibrate) navigator.vibrate(40); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const incCC = async () => { if (!ccLineId) return addCC(); try { await api.patch(`/depot/${ccLineId}`, { quantity: ccQty + 1 }); setCcQty(ccQty + 1); } catch (e) { toast.error(errMsg(e)); } };
  const decCC = async () => { if (!ccLineId) return; const n = ccQty - 1; try { if (n <= 0) { await api.delete(`/depot/${ccLineId}`); setCcQty(0); setCcLineId(null); toast.success("Removed from C&C"); } else { await api.patch(`/depot/${ccLineId}`, { quantity: n }); setCcQty(n); } } catch (e) { toast.error(errMsg(e)); } };

  const load = async () => { try { const { data } = await api.get("/products"); setProducts(data); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { load(); }, []);

  const [params] = useSearchParams();
  useEffect(() => {
    const qp = params.get("q");
    if (qp) setQuery(qp);
    if (params.get("cam")) setCameraOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ping = () => api.post("/presence/ping", { name: staffName, color: activeStaff?.color }).catch(() => {});
    ping(); const iv = setInterval(ping, 12000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readQueue = () => JSON.parse(localStorage.getItem("zc_offline_queue") || "[]");
  const setQueue = (q) => { localStorage.setItem("zc_offline_queue", JSON.stringify(q)); setQueueN(q.length); };
  const flushQueue = async () => {
    const q = readQueue(); if (!q.length) return;
    let ok = 0;
    for (const item of q) { try { await api.post("/basket/add", item); ok++; } catch { /* keep */ } }
    setQueue([]); if (ok) toast.success(`📶 Back online — synced ${ok} offline item(s)`);
  };
  useEffect(() => {
    setQueueN(readQueue().length);
    const on = () => { setOffline(false); flushQueue(); };
    const off = () => { setOffline(true); toast.warning("📶 Offline Mode — scans will sync when back online"); };
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // #1/#2 Barcode scan → open the matched product card AND increment its cart quantity (+1). Unmatched → manual add.
  const onBarcode = async (code) => {
    setCameraOpen(false);
    if (!code) return;
    if (navigator.vibrate) navigator.vibrate(60);
    try {
      const { data } = await api.get("/products/lookup", { params: { barcode: code } });
      await api.post("/basket/add", { product_id: data.id, quantity: 1, unit: "case", variant: "plain", added_by: staffName });
      toast.success(`Scanned: ${data.name} • +1 to cart`);
      focusProduct(data);
    } catch (e) {
      if (e?.response?.status === 404) {
        // Not in the local catalogue → try Open Food Facts (short timeout) before photo/manual entry.
        let off = null;
        try { off = (await api.get("/products/off-lookup", { params: { barcode: code }, timeout: 6000 })).data; } catch { /* fall back */ }
        if (off?.found) { toast.success(`Found online: ${off.name} — check details & add`); navigate("/photo-entry/new", { state: { barcode: code, off } }); }
        else { toast.info("New item — add it to the catalogue"); navigate("/photo-entry/new", { state: { barcode: code } }); }
      }
      else toast.error(errMsg(e));
    }
  };

  const quickAdd = async (p, e) => {
    e.stopPropagation();
    const payload = { product_id: p.id, quantity: 1, unit: "case", variant: "plain", added_by: staffName };
    if (offline || !navigator.onLine) { const q = readQueue(); q.push(payload); setQueue(q); toast.success("Saved offline — will sync"); setAdded(true); setTimeout(() => setAdded(false), 1400); return; }
    try {
      const { data } = await api.post("/basket/add", payload);
      toast.success(data.merged ? `Merged with ${data.merged_with || "existing"}'s entry` : `Added • ${supName(data.routed_to)}`);
      setAdded(true); setTimeout(() => setAdded(false), 1400); if (navigator.vibrate) navigator.vibrate(40);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const search = async (text) => {
    const term = (typeof text === "string" ? text : query).trim();
    if (!term) return;
    try { const { data } = await api.get("/products/lookup", { params: { q: term } }); focusProduct(data); }
    catch (e) {
      if (e?.response?.status === 404) { setFocused(null); setNotFound(term); }
      else toast.error(errMsg(e));
    }
  };

  const notifyRestock = async (id) => { try { const { data } = await api.post("/restock/notify", { product_id: id }); toast.success(data.message); } catch (e) { toast.error(errMsg(e)); } };

  const addManualItem = async () => {
    const term = (notFound || "").trim();
    const bc = /^\d{6,}$/.test(term) ? term : "";
    let off = null;
    if (bc) { try { off = (await api.get("/products/off-lookup", { params: { barcode: bc }, timeout: 6000 })).data; } catch { /* fall back */ } }
    navigate("/photo-entry/new", { state: off?.found ? { barcode: bc, off } : { barcode: bc } });
  };

  const q = query.trim().toLowerCase();
  const filtered = products.filter((p) => {
    if (q) { const hay = `${p.name || ""} ${p.sku || ""} ${p.barcode || ""}`.toLowerCase(); return hay.includes(q); }
    if (cat) {
      const pill = CATEGORY_PILLS.find((c) => c.id === cat);
      if (pill?.id === "offer" && !p.on_offer) return false;
      if (pill?.temp && p.temperature !== pill.temp) return false;
      if (pill?.category && p.category !== pill.category) return false;
    }
    return true;
  });

  const NoProducts = () => (
    <div data-testid="no-products-found" className="text-center py-14 px-4 flex flex-col items-center">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-3"><Package className="w-8 h-8 text-slate-300" /></div>
      <p className="font-bold text-slate-900 text-lg">No Products Available</p>
      <p className="text-sm text-slate-500 mt-1 mb-6 max-w-xs">{(notFound || query.trim()) ? `Nothing matched “${(notFound || query).trim()}”.` : "No products to show here."} Clear the search to browse, or add it manually below.</p>
      <Button data-testid="take-photo-cc-btn" onClick={addManualItem} className="h-12 px-6 bg-[#EA580C] hover:bg-[#c2410c] text-white font-bold gap-2"><Camera className="w-5 h-5" /> + Add Manual Item (Take Photo)</Button>
    </div>
  );

  return (
    <div className="space-y-4 fade-up pb-32 zb-form">
      {added && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none" data-testid="add-success-check">
          <div className="pop-check w-24 h-24 rounded-full bg-emerald-500 flex items-center justify-center shadow-2xl"><Check className="w-12 h-12 text-white" strokeWidth={3} /></div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold font-head text-slate-900">{t("scanTitle")}</h1>
          <p className="text-slate-500 text-sm">{t("scanSub")}</p>
        </div>
        {offline && <span data-testid="offline-badge" className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700"><WifiOff className="w-3.5 h-3.5" /> Offline Mode{queueN > 0 ? ` · ${queueN}` : ""}</span>}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input data-testid="scanner-search-input" placeholder={t("searchPh")} value={query}
          onChange={(e) => { setQuery(e.target.value); setNotFound(null); if (focused) setFocused(null); }} onKeyDown={(e) => e.key === "Enter" && search()} className="pl-9 pr-28 h-12 bg-white border-slate-200" />
        {query && <button data-testid="search-clear" onClick={clearFocus} className="absolute right-[4.75rem] top-1/2 -translate-y-1/2 w-7 h-7 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center active:scale-90"><X className="w-3.5 h-3.5" /></button>}
        <button data-testid="search-voice-icon" aria-label="Voice search" onClick={voiceSearch} className={`absolute right-10 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center active:scale-90 transition-colors ${listening ? "bg-red-500 text-white animate-pulse" : "bg-emerald-500 text-white"}`}><Mic className="w-3.5 h-3.5" /></button>
        <button data-testid="search-scanner-icon" onClick={() => setCameraOpen(true)} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md bg-[#1E3A8A] text-white flex items-center justify-center active:scale-90"><ScanLine className="w-3.5 h-3.5" /></button>
      </div>

      {!q && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="category-pills">
          {CATEGORY_PILLS.map((c) => (
            <button key={c.id} data-testid={`cat-pill-${c.id}`} onClick={() => setCat(cat === c.id ? null : c.id)}
              className={`shrink-0 text-xs font-bold px-3 py-2 rounded-full border transition-all ${cat === c.id ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200"}`}>{c.id === "offer" ? t("catOffer") : t("cat" + c.id)}</button>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">{t("products")} ({focused ? 1 : filtered.length})</p>
        {focused ? (
          <div data-testid={`focused-card-${focused.id}`} className="bg-white rounded-2xl p-3 border-2 border-emerald-500 shadow-sm space-y-3">
            <div className="flex gap-3">
              <button data-testid="focused-image" onClick={() => setZoomSrc(focused.image)} className="relative w-20 h-20 rounded-xl overflow-hidden bg-slate-100 shrink-0 active:scale-95">
                <img src={focused.image} alt={focused.name} className="w-full h-full object-cover" />
                {focused.on_offer && <span className="absolute top-1 left-1 bg-emerald-600 text-white text-[9px] font-extrabold px-1 py-0.5 rounded">{focused.promo_pct}% OFF</span>}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <button data-testid="focused-open-info" onClick={() => navigate(`/product/${focused.id}`)} className="text-left min-w-0"><p className="text-sm font-bold text-slate-900 leading-tight line-clamp-2">{focused.name}</p></button>
                  <button data-testid="focused-clear" onClick={(e) => { e.stopPropagation(); clearFocus(); }} className="shrink-0 w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center"><X className="w-4 h-4 text-slate-500" /></button>
                </div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <StockTag inStock={focused.in_stock} />
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">POR {focused.por}%</span>
                  {focused.low_margin && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">⚠️ Low Margin</span>}
                </div>
              </div>
            </div>
            <div data-testid="supplier-breakdown" className="space-y-1.5">
              <p className="text-[11px] font-bold uppercase text-slate-400">Supplier price comparison (per case)</p>
              {focused.supplier_prices?.map((sp) => (
                <button key={sp.supplier} data-testid={`sup-price-${sp.supplier}`} onClick={() => setFSup(sp.supplier)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-xl border text-sm transition-all ${fSup === sp.supplier ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                  <span className="font-semibold" style={{ color: supColor(sp.supplier) }}>{sp.supplier}{sp.best && <span className="ml-1.5 text-[10px] font-extrabold text-emerald-600">Best Price 🔥</span>}</span>
                  <span className="font-mono font-bold text-slate-900">£{sp.net_price.toFixed(2)}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              {(focused.units_per_case || 1) > 1 && (
                <button data-testid="focused-unit-btn" onClick={() => setFunit("loose")} className={`flex-1 h-9 rounded-xl text-sm font-bold border ${funit === "loose" ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-white text-slate-700 border-slate-200"}`}>Unit</button>
              )}
              <button data-testid="focused-case-btn" onClick={() => setFunit("case")} className={`flex-1 h-9 rounded-xl text-sm font-bold border ${funit === "case" ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-white text-slate-700 border-slate-200"}`}>Case of {focused.units_per_case}</button>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="focused-fav" onClick={() => setFav((v) => !v)} className="w-11 h-12 flex items-center justify-center shrink-0"><Heart className={`w-5 h-5 ${fav ? "text-red-500 fill-red-500" : "text-slate-300"}`} /></button>
              {ccQty === 0 ? (
                <Button data-testid="focused-cc-btn" onClick={addCC} disabled={!focused.in_stock} title="Add to Cash & Carry" className="h-12 px-3 bg-[#EA580C] hover:bg-[#c2410c] text-white font-bold gap-1.5 disabled:opacity-50"><Warehouse className="w-4 h-4" /> C&amp;C</Button>
              ) : (
                <div data-testid="focused-cc-counter" className="flex items-center justify-between bg-[#EA580C] rounded-xl px-1.5 h-12 gap-1">
                  <button data-testid="focused-cc-minus" onClick={decCC} className="w-9 h-9 rounded-lg bg-white/25 text-white flex items-center justify-center active:scale-90"><Minus className="w-4 h-4" /></button>
                  <span data-testid="focused-cc-qty" className="min-w-[20px] text-center font-extrabold font-mono text-white">{ccQty}</span>
                  <button data-testid="focused-cc-plus" onClick={incCC} className="w-9 h-9 rounded-lg bg-white/25 text-white flex items-center justify-center active:scale-90"><Plus className="w-4 h-4" /></button>
                </div>
              )}
              {fqty === 0 ? (
                <Button data-testid="focused-add-btn" onClick={addOne} disabled={!focused.in_stock} className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-1.5 disabled:opacity-50"><Plus className="w-4 h-4" /> {focused.in_stock ? "Add" : "Out"}</Button>
              ) : (
                <div data-testid="focused-counter" className="flex-1 flex items-center justify-between bg-emerald-600 rounded-xl px-2 h-12">
                  <button data-testid="focused-minus" onClick={decFocused} className="w-9 h-9 rounded-lg bg-white/20 text-white flex items-center justify-center active:scale-90"><Minus className="w-4 h-4" /></button>
                  <span data-testid="focused-qty" className="text-center font-extrabold font-mono text-white">{fqty}</span>
                  <button data-testid="focused-plus" onClick={incFocused} className="w-9 h-9 rounded-lg bg-white/20 text-white flex items-center justify-center active:scale-90"><Plus className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            <div data-testid="focused-open-info-footer" onClick={() => navigate(`/product/${focused.id}`)} className="flex items-center justify-between text-[10px] text-slate-400 font-mono border-t border-slate-100 pt-2 cursor-pointer">
              <span className="flex items-center gap-1"><ScanLine className="w-3 h-3" /> {focused.barcode}</span>
              <span>SKU {focused.sku}</span>
            </div>
          </div>
        ) : (notFound || filtered.length === 0) ? (
          <NoProducts />
        ) : filtered.map((p) => (
          <div key={p.id} data-testid={`product-card-${p.id}`}
            className={`flex gap-3 bg-white rounded-2xl p-3 transition-all ${p.on_offer ? "border-2 border-emerald-500" : "border border-slate-200 shadow-sm"}`}>
            <button data-testid={`product-image-${p.id}`} onClick={() => setZoomSrc(p.image)} className="relative w-20 h-20 rounded-xl overflow-hidden bg-slate-100 shrink-0 active:scale-95">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
              {p.on_offer && <span className="absolute top-1 left-1 bg-emerald-600 text-white text-[9px] font-extrabold px-1 py-0.5 rounded">{p.promo_pct}% OFF</span>}
            </button>
            <div className="flex-1 min-w-0">
              <button data-testid={`product-text-${p.id}`} onClick={() => navigate(`/product/${p.id}`)} className="text-left w-full active:opacity-70">
                <p className="text-sm font-bold text-slate-900 leading-tight line-clamp-2">{p.name}</p>
                <p className="text-[10px] text-slate-400 font-mono">SKU {p.sku} · {p.barcode}</p>
              </button>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <SupplierBadge name={p.best_supplier} />
                <StockTag inStock={p.in_stock} />
                <span data-testid={`por-badge-${p.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">POR {p.por}%</span>
                {p.recently_ordered_days != null && <span data-testid={`dup-warn-${p.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">⚠️ Ordered {p.recently_ordered_days}d ago</span>}
                {p.low_margin && <span data-testid={`low-margin-${p.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">⚠️ Low Margin</span>}
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <div>
                  {p.on_offer && <span className="text-slate-400 line-through text-xs mr-1">£{p.best_gross?.toFixed(2)}</span>}
                  <span className="text-emerald-600 font-extrabold font-mono">£{p.best_price?.toFixed(2)}</span>
                  <span className="text-[9px] text-slate-400 ml-0.5">ex VAT · {p.vat_label}</span>
                </div>
                {p.in_stock ? (
                  <button data-testid={`add-btn-${p.id}`} onClick={(e) => quickAdd(p, e)} className="h-8 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center gap-1 active:scale-95"><Plus className="w-3.5 h-3.5" /> {t("add")}</button>
                ) : (
                  <button data-testid={`notify-btn-${p.id}`} onClick={(e) => { e.stopPropagation(); notifyRestock(p.id); }} className="h-8 px-3 rounded-lg bg-slate-900 text-white text-xs font-bold flex items-center gap-1 active:scale-95"><Bell className="w-3.5 h-3.5" /> {t("notifyMe")}</button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <ScannerCamera open={cameraOpen} mode="scan" onDetected={onBarcode} onClose={() => setCameraOpen(false)} demoProducts={products} />
      {zoomSrc && <ImageZoom src={zoomSrc} onClose={() => setZoomSrc(null)} />}
    </div>
  );
}
